import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { groupService } from '../../services/group.service'
import { organizationService } from '../../services/organization.service'
import { PermissionAction, PermissionType } from '../../services/permissions.service'
import { toastService } from '../../services/toast.service'
import { Group, Organization } from '../../types'
import {
  ActiveDirectoryConfig,
  ActiveDirectoryConfigPatch,
  activeDirectoryService,
  ADUserRecord,
} from '../../services/active-directory.service'
import { DocumentationDialog } from '../common/DocumentationDialog'
import { IfPermissions } from '../common/IfPermissions'
import { InputSwitch } from '../common/InputSwitch'

type FormValues = ActiveDirectoryConfigPatch & { bind_password?: string }

const KEEP_PASSWORD_PLACEHOLDER = '__keep__'

function isDirty(initial: ActiveDirectoryConfig | null, form: FormValues): boolean {
  if (!initial) return false
  const fields: (keyof ActiveDirectoryConfig)[] = [
    'is_enabled',
    'server_uri',
    'use_ssl',
    'use_start_tls',
    'validate_cert',
    'ca_cert_pem',
    'bind_dn',
    'user_search_base',
    'user_search_filter',
    'attr_username',
    'attr_email',
    'attr_first_name',
    'attr_last_name',
    'auto_create_users',
    'sync_attributes_on_login',
    'timeout_seconds',
  ]
  for (const key of fields) {
    const initialValue = initial[key]
    const formValue = (form as any)[key]
    if (formValue !== undefined && formValue !== initialValue) {
      return true
    }
  }
  const initialGroups = JSON.stringify([...(initial.default_group_ids ?? [])].sort())
  const formGroups = JSON.stringify([...((form.default_group_ids as number[]) ?? [])].sort())
  if (initialGroups !== formGroups) return true
  const initialOrgs = JSON.stringify([...(initial.default_organization_ids ?? [])].sort())
  const formOrgs = JSON.stringify([...((form.default_organization_ids as number[]) ?? [])].sort())
  if (initialOrgs !== formOrgs) return true
  if (form.bind_password !== undefined && form.bind_password !== KEEP_PASSWORD_PLACEHOLDER) {
    return true
  }
  return false
}

export const SettingsActiveDirectorySection: React.FC = () => {
  const { t } = useTranslation()
  const [config, setConfig] = useState<ActiveDirectoryConfig | null>(null)
  const [form, setForm] = useState<FormValues>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testMessage, setTestMessage] = useState<{ ok: boolean; text: string } | null>(null)
  const [groups, setGroups] = useState<Group[]>([])
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [docsDialogSections, setDocsDialogSections] = useState<string[] | null>(null)
  const [showImportModal, setShowImportModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchResults, setSearchResults] = useState<ADUserRecord[]>([])
  const [searchError, setSearchError] = useState<string | null>(null)
  const [selectedUsernames, setSelectedUsernames] = useState<Set<string>>(new Set())
  const [importing, setImporting] = useState(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const reload = async () => {
    setLoading(true)
    try {
      const data = await activeDirectoryService.getConfig()
      if (!isMountedRef.current) return
      setConfig(data)
      setForm(
        data
          ? {
              is_enabled: data.is_enabled,
              server_uri: data.server_uri,
              use_ssl: data.use_ssl,
              use_start_tls: data.use_start_tls,
              validate_cert: data.validate_cert,
              ca_cert_pem: data.ca_cert_pem,
              bind_dn: data.bind_dn,
              bind_password: data.bind_password_set ? KEEP_PASSWORD_PLACEHOLDER : '',
              user_search_base: data.user_search_base,
              user_search_filter: data.user_search_filter,
              attr_username: data.attr_username,
              attr_email: data.attr_email,
              attr_first_name: data.attr_first_name,
              attr_last_name: data.attr_last_name,
              auto_create_users: data.auto_create_users,
              sync_attributes_on_login: data.sync_attributes_on_login,
              default_group_ids: [...(data.default_group_ids ?? [])],
              default_organization_ids: [...(data.default_organization_ids ?? [])],
              timeout_seconds: data.timeout_seconds,
            }
          : {}
      )
    } catch {
      if (isMountedRef.current) {
        toastService.showError(t('ad.error_loading'))
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    reload()
    groupService
      .listAll()
      .then((res) => {
        if (isMountedRef.current) setGroups(res.results ?? [])
      })
      .catch(() => {
        /* groups are optional for AD section */
      })
    organizationService
      .listAll({ page_size: 100000 })
      .then((res) => {
        if (isMountedRef.current) setOrganizations(res.results ?? [])
      })
      .catch(() => {
        /* organizations are optional for AD section */
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleField = (key: keyof FormValues, value: unknown) => {
    setForm((prev) => ({ ...prev, [key]: value as any }))
  }

  const dirty = useMemo(() => isDirty(config, form), [config, form])

  const buildPatchForSave = (): ActiveDirectoryConfigPatch => {
    const patch: ActiveDirectoryConfigPatch = { ...form }
    if (patch.bind_password === KEEP_PASSWORD_PLACEHOLDER) {
      delete patch.bind_password
    }
    return patch
  }

  const buildOverridesForTest = (): ActiveDirectoryConfigPatch => {
    const overrides: ActiveDirectoryConfigPatch = { ...form }
    if (overrides.bind_password === KEEP_PASSWORD_PLACEHOLDER) {
      delete overrides.bind_password
    }
    return overrides
  }

  const handleSave = async () => {
    if (!config) return
    setSaving(true)
    try {
      const patch = buildPatchForSave()
      const updated = await activeDirectoryService.updateConfig(config.id, patch)
      setConfig(updated)
      setForm((prev) => ({
        ...prev,
        bind_password: updated.bind_password_set ? KEEP_PASSWORD_PLACEHOLDER : '',
      }))
      toastService.showSuccess(t('ad.saved'))
    } catch (err: any) {
      const detail = err?.response?.data?.bind_password?.[0] || err?.response?.data?.detail || t('ad.save_failed')
      toastService.showError(detail)
    } finally {
      setSaving(false)
    }
  }

  const handleDiscard = () => {
    if (!config) return
    reload()
  }

  const handleTest = async () => {
    setTesting(true)
    setTestMessage(null)
    try {
      const result = await activeDirectoryService.testConnection(buildOverridesForTest())
      setTestMessage({ ok: result.success, text: result.message })
    } finally {
      setTesting(false)
    }
  }

  const openImportModal = async () => {
    setShowImportModal(true)
    setSearchQuery('')
    setSearchResults([])
    setSearchError(null)
    setSelectedUsernames(new Set())
    await runSearch('')
  }

  const openBrowseModal = () => {
    setShowImportModal(true)
    setSearchQuery('')
    setSearchResults([])
    setSearchError(null)
    setSelectedUsernames(new Set())
  }

  const runSearch = async (query: string) => {
    setSearchLoading(true)
    setSearchError(null)
    try {
      const result = await activeDirectoryService.searchUsers(query, buildOverridesForTest())
      if (result.success) {
        setSearchResults(result.users)
      } else {
        setSearchError(result.message || t('ad.search_failed'))
        setSearchResults([])
      }
    } finally {
      setSearchLoading(false)
    }
  }

  const handleImport = async () => {
    if (selectedUsernames.size === 0) return
    setImporting(true)
    try {
      const result = await activeDirectoryService.importUsers(Array.from(selectedUsernames))
      const created = result.results.filter((r) => r.status === 'created').length
      const updated = result.results.filter((r) => r.status === 'updated').length
      const failed = result.results.filter((r) => r.status === 'error' || r.status === 'not_found').length
      if (failed === 0) {
        toastService.showSuccess(t('ad.import_summary', { created, updated }))
      } else {
        toastService.showError(t('ad.import_partial', { created, updated, failed }))
      }
      setShowImportModal(false)
    } catch {
      toastService.showError(t('ad.import_failed'))
    } finally {
      setImporting(false)
    }
  }

  const toggleSelected = (username: string) => {
    setSelectedUsernames((prev) => {
      const next = new Set(prev)
      if (next.has(username)) next.delete(username)
      else next.add(username)
      return next
    })
  }

  const selectAllVisible = (checked: boolean) => {
    if (checked) {
      const importable = searchResults.filter((u) => !u.exists_in_dms).map((u) => u.username)
      setSelectedUsernames(new Set(importable))
    } else {
      setSelectedUsernames(new Set())
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t('loading')}</span>
        </Spinner>
      </div>
    )
  }

  if (!config) {
    return <Alert variant="danger">{t('ad.error_loading')}</Alert>
  }

  const usingPlainLdap = !!form.server_uri && form.server_uri.startsWith('ldap://') && !form.use_ssl && !form.use_start_tls
  const isEnabled = !!form.is_enabled

  return (
    <IfPermissions action={PermissionAction.Change} type={PermissionType.AppConfig}>
      <Form
        onSubmit={(e) => {
          e.preventDefault()
          handleSave()
        }}
      >
        <div className="settings-card">
          <div className="settings-card__body">
            <h5>{t('ad.title')}</h5>
            <p className="text-muted small">{t('ad.description')}</p>

            <Row className="g-3 mb-4">
              <Col md={4}>
                <Card>
                  <Card.Body>
                    <h6 className="mb-2">{t('ad.enable_authentication')}</h6>
                    <InputSwitch
                      id="ad-enabled"
                      horizontal
                      value={isEnabled}
                      onChange={(v) => handleField('is_enabled', v)}
                      title={isEnabled ? t('enabled') : t('disabled')}
                    />
                    <div className="text-muted small mt-2">{t('ad.enable_help')}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={8}>
                <Card>
                  <Card.Body>
                    <h6 className="mb-3">{t('ad.connection')}</h6>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('ad.server_uri')}</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="ldap://192.168.2.20:389"
                        value={form.server_uri ?? ''}
                        onChange={(e) => handleField('server_uri', e.target.value)}
                      />
                      <Form.Text className="text-muted">{t('ad.server_uri_help')}</Form.Text>
                    </Form.Group>
                    <Row className="g-2">
                      <Col md={4}>
                        <Form.Check
                          type="checkbox"
                          id="ad-use-ssl"
                          label={t('ad.use_ssl')}
                          checked={!!form.use_ssl}
                          onChange={(e) => handleField('use_ssl', e.target.checked)}
                        />
                      </Col>
                      <Col md={4}>
                        <Form.Check
                          type="checkbox"
                          id="ad-use-start-tls"
                          label={t('ad.use_start_tls')}
                          checked={!!form.use_start_tls}
                          onChange={(e) => handleField('use_start_tls', e.target.checked)}
                        />
                      </Col>
                      <Col md={4}>
                        <Form.Check
                          type="checkbox"
                          id="ad-validate-cert"
                          label={t('ad.validate_cert')}
                          checked={form.validate_cert !== false}
                          onChange={(e) => handleField('validate_cert', e.target.checked)}
                        />
                      </Col>
                    </Row>
                    {usingPlainLdap && (
                      <Alert variant="warning" className="mt-3 mb-0 small">
                        {t('ad.warning_no_tls')}
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Card className="mb-4">
              <Card.Body>
                <h6 className="mb-3">{t('ad.service_account')}</h6>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center gap-1">
                        <span>{t('ad.bind_dn')}</span>
                        <a
                          className="btn btn-sm btn-link p-0"
                          href="#"
                          title={t('read_the_documentation_about_this_setting')}
                          onClick={(e) => {
                            e.preventDefault()
                            setDocsDialogSections([t('ad.bind_dn')])
                          }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </a>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="docdot_test@ewe.rs"
                        value={form.bind_dn ?? ''}
                        onChange={(e) => handleField('bind_dn', e.target.value)}
                      />
                      <Form.Text className="text-muted">{t('ad.bind_dn_help')}</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t('ad.bind_password')}</Form.Label>
                      <Form.Control
                        type="password"
                        autoComplete="new-password"
                        placeholder={config.bind_password_set ? '••••••••' : ''}
                        value={form.bind_password === KEEP_PASSWORD_PLACEHOLDER ? '' : (form.bind_password ?? '')}
                        onChange={(e) => handleField('bind_password', e.target.value)}
                        onFocus={() => {
                          if (form.bind_password === KEEP_PASSWORD_PLACEHOLDER) {
                            handleField('bind_password', '')
                          }
                        }}
                      />
                      {config.bind_password_set && form.bind_password === KEEP_PASSWORD_PLACEHOLDER && (
                        <Form.Text className="text-muted">{t('ad.bind_password_keep_help')}</Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Body>
                <h6 className="mb-3">{t('ad.user_lookup')}</h6>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex align-items-center gap-1">
                    <span>{t('ad.user_search_base')}</span>
                    <a
                      className="btn btn-sm btn-link p-0"
                      href="#"
                      title={t('read_the_documentation_about_this_setting')}
                      onClick={(e) => {
                        e.preventDefault()
                        setDocsDialogSections([t('ad.user_search_base')])
                      }}
                    >
                      <i className="bi bi-info-circle"></i>
                    </a>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="OU=Users,OU=Sve firme,DC=ewe,DC=rs"
                    value={form.user_search_base ?? ''}
                    onChange={(e) => handleField('user_search_base', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('ad.user_search_filter')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.user_search_filter ?? ''}
                    onChange={(e) => handleField('user_search_filter', e.target.value)}
                  />
                  <Form.Text className="text-muted">{t('ad.user_search_filter_help')}</Form.Text>
                </Form.Group>
                <Row className="g-3">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>{t('ad.attr_username')}</Form.Label>
                      <Form.Control
                        type="text"
                        value={form.attr_username ?? ''}
                        onChange={(e) => handleField('attr_username', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>{t('ad.attr_email')}</Form.Label>
                      <Form.Control
                        type="text"
                        value={form.attr_email ?? ''}
                        onChange={(e) => handleField('attr_email', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>{t('ad.attr_first_name')}</Form.Label>
                      <Form.Control
                        type="text"
                        value={form.attr_first_name ?? ''}
                        onChange={(e) => handleField('attr_first_name', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>{t('ad.attr_last_name')}</Form.Label>
                      <Form.Control
                        type="text"
                        value={form.attr_last_name ?? ''}
                        onChange={(e) => handleField('attr_last_name', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Body>
                <h6 className="mb-3">{t('ad.provisioning')}</h6>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="ad-auto-create"
                      label={t('ad.auto_create_users')}
                      checked={!!form.auto_create_users}
                      onChange={(e) => handleField('auto_create_users', e.target.checked)}
                    />
                    <Form.Text className="text-muted">{t('ad.auto_create_users_help')}</Form.Text>
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="ad-sync-attrs"
                      label={t('ad.sync_attributes_on_login')}
                      checked={!!form.sync_attributes_on_login}
                      onChange={(e) => handleField('sync_attributes_on_login', e.target.checked)}
                    />
                  </Col>
                </Row>
                <Form.Group className="mt-3">
                  <Form.Label>{t('ad.default_organizations')}</Form.Label>
                  <div className="docdot-input-select">
                    <Select
                      isMulti
                      options={organizations.map((o) => ({ value: o.id, label: o.name }))}
                      value={organizations
                        .filter((o) => (form.default_organization_ids ?? []).includes(o.id))
                        .map((o) => ({ value: o.id, label: o.name }))}
                      onChange={(selected) => {
                        const ids = selected ? selected.map((o) => o.value) : []
                        handleField('default_organization_ids', ids)
                      }}
                      placeholder={t('select_organizations')}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      isClearable={false}
                      closeMenuOnSelect={false}
                    />
                  </div>
                  <Form.Text className="text-muted">{t('ad.default_organizations_help')}</Form.Text>
                </Form.Group>

                <Form.Group className="mt-3">
                  <Form.Label>{t('ad.default_groups')}</Form.Label>
                  <div className="docdot-input-select">
                    <Select
                      isMulti
                      options={groups.map((g) => ({ value: g.id, label: g.name }))}
                      value={groups
                        .filter((g) => (form.default_group_ids ?? []).includes(g.id))
                        .map((g) => ({ value: g.id, label: g.name }))}
                      onChange={(selected) => {
                        const ids = selected ? selected.map((o) => o.value) : []
                        handleField('default_group_ids', ids)
                      }}
                      placeholder={t('select_groups')}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      isClearable={false}
                      closeMenuOnSelect={false}
                    />
                  </div>
                  <Form.Text className="text-muted">{t('ad.default_groups_help')}</Form.Text>
                </Form.Group>
                <Form.Group className="mt-3" controlId="ad-timeout">
                  <Form.Label>{t('ad.timeout_seconds')}</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    max={60}
                    value={String(form.timeout_seconds ?? 5)}
                    onChange={(e) => handleField('timeout_seconds', Number(e.target.value) || 5)}
                    style={{ maxWidth: '120px' }}
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Body>
                <h6 className="mb-3">{t('ad.diagnostics')}</h6>
                <div className="d-flex gap-2 flex-wrap">
                  <Button variant="outline-primary" type="button" onClick={handleTest} disabled={testing}>
                    {testing && <Spinner size="sm" className="me-2" />} {t('ad.test_connection')}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={openBrowseModal}
                    disabled={!isEnabled || dirty}
                    title={dirty ? t('ad.save_before_import') : ''}
                  >
                    {t('ad.browse_users_button')}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={openImportModal}
                    disabled={!isEnabled || dirty}
                    title={dirty ? t('ad.save_before_import') : ''}
                  >
                    {t('ad.import_users_button')}
                  </Button>
                </div>
                {testMessage && (
                  <Alert variant={testMessage.ok ? 'success' : 'danger'} className="mt-3 mb-0">
                    {testMessage.text}
                  </Alert>
                )}
                {config.last_test_at && !testMessage && (
                  <div className="text-muted small mt-2">
                    {t('ad.last_test', {
                      when: new Date(config.last_test_at).toLocaleString(),
                      result: config.last_test_ok ? t('ad.test_ok') : t('ad.test_failed'),
                    })}
                    {config.last_test_message && <>: {config.last_test_message}</>}
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>

        <div className="d-flex gap-2 justify-content-end mt-3 mb-3">
          <Button type="button" variant="outline-secondary" disabled={!dirty || saving} onClick={handleDiscard}>
            {t('cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={!dirty || saving}>
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" /> {t('saving')}
              </>
            ) : (
              t('save')
            )}
          </Button>
        </div>
      </Form>

      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{t('ad.search_modal_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault()
              runSearch(searchQuery)
            }}
            className="d-flex gap-2 mb-3"
          >
            <Form.Control
              type="text"
              placeholder={t('ad.search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Button type="submit" variant="outline-primary" disabled={searchLoading}>
              {searchLoading ? <Spinner size="sm" /> : t('search')}
            </Button>
          </Form>

          {searchError && <Alert variant="danger">{searchError}</Alert>}

          {!searchError && (
            <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
              <Table hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>
                      <Form.Check
                        type="checkbox"
                        checked={selectedUsernames.size > 0 && selectedUsernames.size === searchResults.filter((u) => !u.exists_in_dms).length}
                        onChange={(e) => selectAllVisible(e.target.checked)}
                        disabled={searchResults.every((u) => u.exists_in_dms)}
                      />
                    </th>
                    <th>{t('ad.col_username')}</th>
                    <th>{t('ad.col_email')}</th>
                    <th>{t('ad.col_name')}</th>
                    <th>{t('ad.col_status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.length === 0 && !searchLoading && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-3">
                        {t('ad.no_results')}
                      </td>
                    </tr>
                  )}
                  {searchResults.map((u) => (
                    <tr key={u.dn}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedUsernames.has(u.username)}
                          disabled={u.exists_in_dms}
                          onChange={() => toggleSelected(u.username)}
                        />
                      </td>
                      <td>{u.username}</td>
                      <td>{u.email}</td>
                      <td>
                        {u.first_name} {u.last_name}
                      </td>
                      <td>
                        {u.exists_in_dms ? (
                          <Badge bg="secondary">{t('ad.status_exists')}</Badge>
                        ) : (
                          <Badge bg="success">{t('ad.status_new')}</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowImportModal(false)} disabled={importing}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={handleImport} disabled={importing || selectedUsernames.size === 0}>
            {importing ? <Spinner size="sm" className="me-2" /> : null}
            {t('ad.import_selected', { count: selectedUsernames.size })}
          </Button>
        </Modal.Footer>
      </Modal>

      <DocumentationDialog
        show={docsDialogSections !== null}
        onHide={() => setDocsDialogSections(null)}
        docKey="active_directory"
        sectionTitles={docsDialogSections ?? []}
      />
    </IfPermissions>
  )
}
